/**
 * IMPORTS
 */
import * as commands from 'src/aggregates/timelines/commands';
import * as handlers from 'src/aggregates/timelines/handlers';
import {execute} from 'src/aggregates/execute';

/**
 * TYPES
 */
import {AckTypes} from 'src/aggregates/conversations/message/messages.d';
import {ITemplateInput} from 'src/aggregates/conversations/queries.d';
import {ITimelineConversation} from 'src/aggregates/timelines/queries.d';
import {IMessage} from 'src/aggregates/timelines/state.d';


/**
 * CODE
 */

/**
 * I am an API to add a timeline event.
 *
 * :param account: account id
 * :param application: conversation current application uid
 * :param contact: contact id of conversation being added
 * :param end: conversation end timestamp
 * :param id: conversation id
 * :param lastInteraction: conversation last interaction timestamp
 * :param messages: timeline event messages
 * :param remote: contact uid
 * :param start: conversation start timestamp
 * :param status: conversation exit status
 * :param subject: conversation subject
 *
 * :returns: promise with nothing
 */
async function add (
    account: number,
    application: string | null,
    contact: number | string,
    end: number | null,
    id: number,
    lastInteraction: number,
    messages: IMessage[],
    remote: string,
    start: number,
    status: number | null,
    subject: string | null,
): Promise<void>
{
    await execute(commands.add(account,
                               application,
                               contact,
                               end,
                               id,
                               lastInteraction,
                               messages,
                               remote,
                               start,
                               status,
                               subject),
                  handlers.add);
}


/*
 * I fetch the messages of a conversation.
 *
 * :param id: conversation id
 *
 * :returns: promise with nothing
 */
async function fetch (id: number)
{
    await execute(commands.fetch(id), handlers.fetch);
}


/**
 * I clear timeline by conversation state
 *
 * :returns: promise with nothing
 */
async function clearByConversation (): Promise<void>
{
    await execute(commands.clearByConversation(), handlers.clearByConversation);
}


/**
 * I am an API to fetch a message by original id.
 *
 * :param message: message id
 * :param reply: original id of message being replied
 * :param conversation: conversation id of replying message
 *
 * :returns: promise with nothing
 */
async function fetchMessageByOriginalId (
    message: number,
    reply: string,
    conversation: number,
): Promise<void>
{
    await execute(
        commands.fetchMessageByOriginalId(message, reply, conversation),
        handlers.fetchMessageByOriginalId,
    );
}


/**
 * I focus on a timeline conversation.
 *
 * :param id: conversation id
 *
 * :returns: promise with nothing
 */
async function focus (id: number): Promise<void>
{
    await execute(commands.focus(id), handlers.focus);
}


/**
 * I am an API to load a contact timeline.
 *
 * :param contact: contact id
 *
 * :returns: promise with nothing
 */
async function loadByContact (contact: number | string): Promise<void>
{
    await execute(commands.loadByContact(contact), handlers.loadByContact);
}


/**
 * I load the messages of a conversation on timeline.
 *
 * :returns: promise with nothing
 */
async function loadNextConversationMessages (): Promise<void>
{
    await execute(
        commands.loadNextConversationMessages(),
        handlers.loadNextConversationMessages);
}


/**
 * I load the messages of a conversation on timeline by conversations.
 *
 * :param conversation: conversation id
 *
 * :returns: promise with nothing
 */
async function loadNextMessagesByConversation (
    conversation: number,
): Promise<void>
{
    await execute(
        commands.loadNextMessagesByConversation(conversation),
        handlers.loadNextMessagesByConversation);
}


/**
 * I am an timeline API to receive a message ack.
 *
 * :param conversationId: conversation id
 * :param messageId: message id
 * :param ack: message ack
 * :param originalId: message new originalId
 *
 * :returns: promise with nothing
 */
async function onMessageAck(
    conversationId: number,
    messageId: number,
    ack: AckTypes,
    originalId: string | null,
): Promise<void>
{
    await execute(commands.onMessageAck(conversationId,
                                        messageId,
                                        ack,
                                        originalId),
                  handlers.onMessageAck);
}


/**
 * I am an API to remove a timeline event.
 *
 * :param id: conversation id
 *
 * :returns: promise with nothing
 */
async function remove (id: number): Promise<void>
{
    await execute(commands.remove(id), handlers.remove);
}


/**
 * I am an api to reset timeline error.
 *
 * :returns: promise with nothing
 */
async function resetError (): Promise<void>
{
    await execute(commands.resetError(), handlers.resetError);
}


/**
 * I am an API to send a timeline template.
 *
 * :param conversation: conversation conversation
 * :param templateInput: template to be sent
 *
 * :returns: promise with nothing
 */
async function sendTemplate(
    conversation: number,
    templateInput: ITemplateInput,
): Promise<void>
{
    await execute(
        commands.sendTemplate(conversation, templateInput),
        handlers.sendTemplate,
    );
}


/**
 * EXPORTS
 */
export {
    add,
    clearByConversation,
    fetch,
    fetchMessageByOriginalId,
    focus,
    loadByContact,
    loadNextConversationMessages,
    loadNextMessagesByConversation,
    onMessageAck,
    remove,
    resetError,
    sendTemplate,
};
